import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { Checkmark20Filled } from "@fluentui/react-icons";
import { FONT_ACCENT, FONT_BODY } from "./theme";

interface Props {
  icon: React.ReactNode;
  label: string;
  helper?: string;
  selected?: boolean;
  onClick?: () => void;
  size?: "md" | "lg";
}

const useStyles = makeStyles({
  card: {
    appearance: "none",
    cursor: "pointer",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    textAlign: "left",
    gap: "10px",
    backgroundColor: "#ffffff",
    color: "var(--sm-ink)",
    fontFamily: FONT_BODY,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    minHeight: "150px",
    ...shorthands.border("2px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    transitionProperty: "transform, border-color, box-shadow, background-color",
    transitionDuration: "180ms",
    ":hover": {
      transform: "translateY(-2px)",
      ...shorthands.borderColor("var(--sm-primary)"),
      boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "3px",
      outlineColor: "var(--sm-accent)",
      outlineOffset: "2px",
    },
  },
  cardLg: {
    minHeight: "190px",
    paddingTop: "26px",
    paddingBottom: "26px",
    paddingLeft: "26px",
    paddingRight: "26px",
  },
  selected: {
    ...shorthands.borderColor("var(--sm-accent)"),
    backgroundColor: "var(--sm-accent-soft)",
    boxShadow: "0 14px 28px rgba(247,168,35,0.20)",
  },
  iconBubble: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "52px",
    height: "52px",
    color: "var(--sm-primary)",
    backgroundColor: "var(--sm-accent-soft)",
    ...shorthands.borderRadius("12px"),
  },
  label: {
    margin: 0,
    fontFamily: FONT_ACCENT,
    fontSize: "1.4rem",
    lineHeight: 1.1,
    color: "var(--sm-primary-deep)",
    fontWeight: 500,
  },
  helper: {
    margin: 0,
    color: "#6c6171",
    fontSize: "0.9rem",
    lineHeight: 1.35,
  },
  checkBadge: {
    position: "absolute",
    top: "12px",
    right: "12px",
    width: "30px",
    height: "30px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--sm-accent)",
    color: "var(--sm-primary-deep)",
    ...shorthands.borderRadius("999px"),
    boxShadow: "0 6px 14px rgba(247,168,35,0.45)",
  },
});

export default function ChoiceCard({
  icon,
  label,
  helper,
  selected = false,
  onClick,
  size = "md",
}: Props) {
  const styles = useStyles();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={mergeClasses(
        styles.card,
        size === "lg" ? styles.cardLg : "",
        selected ? styles.selected : "",
      )}
    >
      <span className={styles.iconBubble}>{icon}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h3 className={styles.label}>{label}</h3>
        {helper && <p className={styles.helper}>{helper}</p>}
      </div>
      {selected && (
        <span className={styles.checkBadge} aria-hidden>
          <Checkmark20Filled />
        </span>
      )}
    </button>
  );
}
